import { CONSOLE_REGISTER_URL } from '../config/endpoints.ts';
import { useT } from '../i18n/index.ts';
import { ArrowRight } from './logos/index.tsx';
import { Terminal } from './Terminal.tsx';

/**
 * Hero — asymmetric 7/5 split (DESIGN.md §6.2).
 *
 * Left: three-line h1 with the middle line as the accent word, a 17px
 * subtitle, two CTA pills (primary ink + ghost), and a trust row of
 * three small olive-check + label pairs.
 *
 * Right: the `Terminal` card on stacked parchment backdrops, with a
 * small caption strip below.
 *
 * Both columns carry the `.rise` mount animation; the right column
 * staggers by 120ms via inline `style` to soften the reveal.
 */
export function Hero(): JSX.Element {
  const t = useT();
  return (
    <section id="top" className="relative overflow-hidden" data-testid="hero">
      {/* Background blur orbs — decorative, no content */}
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute -right-24 -top-24 h-[520px] w-[520px] rounded-full bg-claude/10 blur-3xl" />
        <div className="absolute -left-24 top-40 h-[420px] w-[420px] rounded-full bg-butter blur-2xl" />
      </div>

      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-x-12 gap-y-16 px-6 pb-28 pt-20 lg:grid-cols-12">
        {/* Left: headline + CTAs + trust row */}
        <div className="rise lg:col-span-7">
          <h1
            className="mb-7 text-[56px] font-semibold leading-[1.25] text-ink md:text-[72px]"
            data-testid="hero-headline"
          >
            {t('hero.title.line1')}
            <br />
            <span className="serif text-claude">{t('hero.title.accent')}</span>
            <br />
            {t('hero.title.line3')}
          </h1>

          <p className="mb-10 max-w-[560px] text-[17px] leading-[1.65] text-ink-muted">
            {t('hero.subtitle')}
          </p>

          <div className="flex flex-wrap items-center gap-3">
            <a
              href={CONSOLE_REGISTER_URL}
              className="group inline-flex items-center gap-2 rounded-full bg-ink px-7 py-3.5 text-[14.5px] font-medium text-linen shadow-warm-md transition-colors hover:bg-ink-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-claude focus-visible:ring-offset-2 focus-visible:ring-offset-linen"
              data-testid="hero-cta-primary"
            >
              {t('hero.cta.primary')}
              <ArrowRight className="h-[14px] w-[14px] transition-transform group-hover:translate-x-0.5" />
            </a>
            <a
              href="#docs"
              className="ghost-strong inline-flex items-center gap-2 rounded-full bg-linen px-6 py-3.5 text-[14.5px] font-medium text-ink transition-colors hover:bg-butter focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-claude focus-visible:ring-offset-2 focus-visible:ring-offset-linen"
              data-testid="hero-cta-secondary"
            >
              {t('hero.cta.secondary')}
              <ArrowRight className="h-[14px] w-[14px]" />
            </a>
          </div>

          <div className="mt-10 flex flex-wrap gap-x-8 gap-y-3 text-[13px] text-ink-muted">
            <TrustItem label={t('hero.trust.no_proxy')} />
            <TrustItem label={t('hero.trust.no_expiry')} />
            <TrustItem label={t('hero.trust.pay_as_you_go')} />
          </div>
        </div>

        {/* Right: terminal card */}
        <div className="rise lg:col-span-5" style={{ animationDelay: '.12s' }}>
          <Terminal />
          <div className="mt-5 flex items-center justify-between px-1 text-[12px] text-ink-subtle">
            <span>{t('hero.terminal.caption.base_url')}</span>
          </div>
        </div>
      </div>
    </section>
  );
}

function TrustItem({ label }: { readonly label: string }): JSX.Element {
  return (
    <span className="inline-flex items-center gap-2">
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        className="text-olive"
        aria-hidden="true"
      >
        <path d="M20 6L9 17l-5-5" />
      </svg>
      {label}
    </span>
  );
}
